import { useEffect, useState } from 'react';
import type { FundingResponse } from '../../shared/contract';
import { loadWithFallback } from '../lib/api';
import { basePath } from '../lib/paths';
import { fmtPrice } from '../lib/format';
import { log } from '../lib/log';
import { useInView } from '../site/motion';
import fundingSnapshot from '../data/funding.json';
import { Flow, type Step } from './Flow';

const FUNDING_FALLBACK = fundingSnapshot as unknown as FundingResponse;

const STEPS: readonly Step[] = [
  {
    title: 'Scan the coins',
    detail: 'Watches all coins constantly for something to happen.',
  },
  {
    title: 'Wait for your alert',
    detail:
      "Once your customized alert is detected, it gets ready to send it. In this instance we'll detect high or low RSI.",
  },
  {
    title: 'Test your alert once detected',
    detail:
      'Once we think your alert was detected, we make sure to backtest and review if something should actually happen.',
  },
  {
    title: 'Build the embed',
    detail: 'We compile the alert into a Discord embed ready for sending. It builds in under one second.',
  },
  {
    title: 'Send the alert',
    detail: 'We post your alert to whichever Discord channel you like.',
  },
];

const COINS = ['SOL', 'AVAX', 'LINK', 'SUI', 'DOGE', 'ADA', 'XRP', 'BNB'] as const;

function pickCoin(): string {
  const index = Math.floor(Math.random() * COINS.length);
  return COINS[index] ?? 'SOL';
}

function clockLabel(): string {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `Today at ${hh}:${mm}`;
}

export function Alerts() {
  const [ref, seen] = useInView<HTMLDivElement>(0.15);
  const [runKey, setRunKey] = useState(0);
  const [step, setStep] = useState(-1);
  const [delivered, setDelivered] = useState(false);
  const [coin, setCoin] = useState(pickCoin);
  const [stamp, setStamp] = useState(clockLabel);
  const [prices, setPrices] = useState<Record<string, number>>({});

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;

    loadWithFallback<FundingResponse>(basePath('/api/funding'), FUNDING_FALLBACK, controller.signal)
      .then((result) => {
        if (!alive) return;
        const map: Record<string, number> = {};
        for (const row of result.data.rows) map[row.base] = row.markPrice;
        setPrices(map);
        log.debug('demo', `alert demo loaded ${Object.keys(map).length} prices`);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        log.warn('demo', 'alert demo could not load prices', err);
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  const replay = () => {
    setDelivered(false);
    setStep(-1);
    setCoin(pickCoin());
    setStamp(clockLabel());
    setRunKey((k) => k + 1);
  };

  const price = prices[coin];

  const trace = [
    'Watching 120 symbols for your conditions',
    `RSI 15 on ${coin}, candidate found`,
    'Backtesting the signal against recent candles',
    'Checks passed, embed built in 0.4s',
  ];

  return (
    <div className="mk-case" ref={ref}>
      <div className="mk-case-head">
        <h2 className="mk-case-title">Automated Discord alerts</h2>
        <p className="mk-case-line">Get alerts in your Discord for stuff happening.</p>
      </div>

      <div className="mk-case-grid">
        <div className="mk-case-flow">
          <Flow
            steps={STEPS}
            active={seen}
            runKey={runKey}
            onStep={setStep}
            onDone={() => setDelivered(true)}
          />
          <button className="mk-replay" onClick={replay}>
            Run it again
          </button>
        </div>

        <div className="mk-case-out">
          <div className="dc" data-shown="true">
            <div className="dc-head">
              <span className="dc-hash" aria-hidden="true">
                #
              </span>
              <span className="dc-channel">alerts</span>
            </div>

            <div className="dc-body al-thread">
              {trace.map((line, i) => (
                <div className="sb-step" data-shown={step >= i ? 'true' : 'false'} key={line}>
                  <span className={i === 3 ? 'sb-chain sb-chain-ok' : 'sb-chain'}>
                    <span className="sb-chain-dot" aria-hidden="true" />
                    {line}
                  </span>
                </div>
              ))}

              <div className="ai-msg" data-shown={delivered ? 'true' : 'false'}>
                <img className="dc-avatar" src="/avatars/yellow.png" alt="" width={40} height={40} />
                <div className="dc-msg-body">
                  <div className="dc-msg-head">
                    <span className="dc-author">steamed bot</span>
                    <span className="dc-tag">APP</span>
                    <span className="dc-time">{stamp}</span>
                  </div>
                  <div className="dc-embed" style={{ borderInlineStartColor: '#6cc79a' }}>
                    <p className="dc-embed-title">{coin} RSI hit 15</p>
                    <p className="dc-embed-sub">{coin}USDT perpetual, heavily oversold</p>
                    <div className="dc-embed-grid">
                      <div className="dc-field">
                        <span className="dc-field-label">RSI</span>
                        <span className="dc-field-value" data-tone="up">
                          15
                        </span>
                      </div>
                      <div className="dc-field">
                        <span className="dc-field-label">Threshold</span>
                        <span className="dc-field-value">20</span>
                      </div>
                      <div className="dc-field">
                        <span className="dc-field-label">Price</span>
                        <span className="dc-field-value">{price ? fmtPrice(price) : '—'}</span>
                      </div>
                      <div className="dc-field">
                        <span className="dc-field-label">Timeframe</span>
                        <span className="dc-field-value">5 minute</span>
                      </div>
                    </div>
                    <p className="dc-embed-foot">Example of the embed this bot posts</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
